// PATH: SaberpDeals/frontend/src/pages/CustomersPage.tsx
import { useEffect, useState } from 'react';
import { customersApi, type Customer, type CustomerInput } from '@/lib/customersApi';
import Modal, { fieldLabel, fieldInput } from '@/components/Modal';

const CURRENCIES = ['USD', 'IQD'] as const;

function initials(name: string) {
    return (name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
}

const emptyForm: CustomerInput = { name: '', erpTenantCode: '', defaultCurrency: 'USD', notes: '' };

export default function CustomersPage() {
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState<Customer | null>(null);
    const [form, setForm] = useState<CustomerInput>(emptyForm);
    const [saveErr, setSaveErr] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try { setCustomers(await customersApi.list()); setError(null); }
        catch { setError('Failed to load customers.'); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openAdd() { setEditing(null); setForm(emptyForm); setSaveErr(null); setShowForm(true); }
    function openEdit(c: Customer) {
        setEditing(c);
        setForm({ name: c.name, erpTenantCode: c.erpTenantCode ?? '', defaultCurrency: c.defaultCurrency, notes: c.notes ?? '' });
        setSaveErr(null); setShowForm(true);
    }

    async function save() {
        setSaving(true); setSaveErr(null);
        try {
            if (editing) await customersApi.update(editing.id, form);
            else await customersApi.create(form);
            setShowForm(false); await load();
        } catch (e: any) { setSaveErr(e?.response?.data?.error ?? 'Could not save.'); }
        finally { setSaving(false); }
    }

    return (
        <div className="mt-6">
            <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="font-disp text-[21px] font-semibold tracking-tight">Customers</div>
                    <div className="text-[13px] text-ink-3 mt-0.5 max-w-xl">
                        Each customer groups their deals — new, upsells, and renewals. Deal history appears here once deals are recorded.
                    </div>
                </div>
                <button onClick={openAdd} className="bg-forest text-paper text-[13px] font-semibold px-4 py-2.5 rounded-lg hover:bg-forest-2 transition">
                    + Add customer
                </button>
            </div>

            {error && <div className="mb-4 rounded-lg bg-clay-soft border border-clay/30 px-4 py-3 text-sm text-clay">{error}</div>}

            {loading ? (
                <div className="text-ink-3 text-sm py-16 text-center">Loading…</div>
            ) : customers.length === 0 ? (
                <div className="text-center py-20 text-ink-3">
                    <div className="font-disp text-4xl mb-3 text-line-2">§</div>
                    <div className="font-disp text-lg font-semibold text-ink-2 mb-1.5">No customers yet</div>
                    <div className="text-sm">Add your first customer.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                    {customers.map((c) => (
                        <div key={c.id} className="bg-card border border-line rounded-2xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-blue2-soft text-blue2 flex items-center justify-center text-xs font-semibold font-mono flex-none">
                                    {initials(c.name)}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-semibold text-[15px] truncate">{c.name}</div>
                                    <div className="text-[11px] text-ink-3 font-mono mt-0.5">
                                        {c.erpTenantCode ? `${c.erpTenantCode} · ` : ''}{c.defaultCurrency}
                                    </div>
                                </div>
                            </div>
                            {c.notes && <div className="text-[12.5px] text-ink-2 mt-2.5">{c.notes}</div>}
                            <div className="flex gap-3 mt-3 pt-3 border-t border-paper-2 text-[12.5px]">
                                <button onClick={() => openEdit(c)} className="text-ink-3 hover:text-forest">Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showForm && (
                <Modal title={editing ? 'Edit customer' : 'Add customer'} onClose={() => setShowForm(false)}>
                    {saveErr && <div className="mb-3 rounded-lg bg-clay-soft border border-clay/30 px-3 py-2 text-[13px] text-clay">{saveErr}</div>}

                    <label className={fieldLabel}>Name</label>
                    <input className={`${fieldInput} mb-3`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nwekar Interior" autoFocus />

                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className={fieldLabel}>ERP code <span className="text-ink-3 normal-case font-normal">(optional)</span></label>
                            <input className={fieldInput} value={form.erpTenantCode ?? ''} onChange={(e) => setForm({ ...form, erpTenantCode: e.target.value })} placeholder="NWK" />
                        </div>
                        <div>
                            <label className={fieldLabel}>Default currency</label>
                            <select className={fieldInput} value={form.defaultCurrency} onChange={(e) => setForm({ ...form, defaultCurrency: e.target.value as any })}>
                                {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    <label className={fieldLabel}>Notes <span className="text-ink-3 normal-case font-normal">(optional)</span></label>
                    <textarea className={`${fieldInput} mb-5 resize-none`} rows={2} value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} />

                    <div className="flex justify-end gap-2">
                        <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-ink-2 rounded-lg border border-line-2 hover:bg-paper">Cancel</button>
                        <button disabled={saving || !form.name} onClick={save} className="px-4 py-2 text-sm font-semibold text-paper bg-forest rounded-lg hover:bg-forest-2 disabled:opacity-50">
                            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add customer'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
